import _ from 'lodash';

/**
 * True when `value` is a dotted-quad IPv4 address.
 *
 * Mirrors the backend catalog's `ipv4` param check so a malformed server
 * address is caught in the dialog rather than as a dispatch rejection.
 *
 * @param {string} value - candidate address, as typed.
 * @returns {boolean} whether all four octets are present and in range.
 */
export const isValidIpv4 = value => {
  const octets = String(value ?? '').split('.');
  if (octets.length !== 4) return false;
  return octets.every(octet => /^\d{1,3}$/.test(octet) && Number(octet) <= 255);
};

export const getCommandKeyFromDisplayName = displayName => {
  if (!displayName) {
    return 'commands.pending_command';
  }
  return `commands.${displayName
    .replace(/ /g, '_')
    .replace(/:/g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/&/g, '')
    .replace(/-/g, '')
    .replace('__', '_')
    .trim()
    .toLowerCase()}`;
};

export const userCommandMap = {
  STATUS_GET_LIVE_POWER: {
    displayName: 'Get Live Power',
  },
  STATUS_GET_LIVE_CURRENT: {
    displayName: 'Get Live Current',
  },
  STATUS_GET_SHORT: {
    displayName: 'Get Status',
  },
  STATUS_GET_ZONEINFO: {
    displayName: 'Get Status',
  },
  RELAY_ON: {
    displayName: 'Relay On',
  },
  RELAY_OFF: {
    displayName: 'Relay Off',
  },
  REBOOT_RELAY: {
    displayName: 'Reboot Relay',
  },
  CIC_IMAGE: {
    displayName: 'Get Image',
  },
  CAMERA: {
    displayName: 'Get Image',
  },
  PAIR_NODE: {
    displayName: 'Pair Device',
  },
  UNPAIR_NODE: {
    displayName: 'Unpair Device',
  },
  ALL_ZONES_ON: {
    displayName: 'All Zones On',
  },
  ALL_ZONES_OFF: {
    displayName: 'All Zones Off',
  },
  ARM_INTRUSION_SENSOR: {
    displayName: 'Arm Intrusion Sensor',
  },
  DISARM_INTRUSION_SENSOR: {
    displayName: 'Disarm Intrusion Sensor',
  },
  OVERRIDE_INTRUSION_SENSOR: {
    displayName: 'Override Intrusion Sensor',
  },
  CONFIGURE_INTRUSION_SENSOR: {
    displayName: 'Configure Intrusion Sensor',
  },
};

export const adminCommandMap = {
  TIME_SET: {
    displayName: 'Set Time',
  },
  SCHEDULE_SET: {
    displayName: 'Set All Schedules',
  },
  SCHEDULE_SET_ONE: {
    displayName: 'Set One Schedule',
  },
  ALARM_SET: {
    displayName: 'Set Alarm',
  },
  RESET: {
    displayName: 'Reboot Unit',
  },
  STATUS_GET_CRC: {
    displayName: 'Get Firmware Checksums',
  },
  STATUS_GET_LONG: {
    displayName: 'Get Long Status',
  },
  STATUS_GET_LIVE_POWER: {
    displayName: 'Get Live Power',
  },
  STATUS_GET_LIVE_CURRENT: {
    displayName: 'Get Live Current',
  },
  STATUS_GET_SHORT: {
    displayName: 'Get Short Status',
  },
  STATUS_GET_V2: {
    displayName: 'Get Status',
  },
  STATUS_GET_IP: {
    displayName: 'Get IP Status',
  },
  STATUS_GET_DATA_REPORT: {
    displayName: 'Get Data Report Status',
  },
  STATUS_GET_ZONEINFO: {
    displayName: 'Request Commercial Status',
  },
  STATUS_GET_RELAY: {
    displayName: 'Request Relay Monitor Status',
  },
  THRESHOLDS_SET: {
    displayName: 'Set Thresholds',
  },
  FACTORY_RESET: {
    displayName: 'Reset to Factory Defaults',
  },
  NETWORK_SET: {
    displayName: 'Set Network Parameters',
  },
  FIRMWARE_UPGRADE: {
    displayName: 'Upgrade Firmware',
  },
  TIMEOUT_SET: {
    displayName: 'Set Timeout',
  },
  RESYNC: {
    displayName: 'Reconfigure Unit',
  },
  SET_DEVICE_CONFIGURATION: {
    displayName: 'Set Device Configuration',
  },
  CIC_CONFIG: {
    displayName: 'Set CIC Configuration',
  },
  CIC_IMAGE: {
    displayName: 'Get Image',
  },
  CAMERA: {
    displayName: 'Get Image',
  },
  CIC_SET_TIME: {
    displayName: 'Set CIC Time',
  },
  CIC_STATUS: {
    displayName: 'Get CIC Status',
  },
  CIC_FACTORY_RESET: {
    displayName: 'Reset CIC to Factory Defaults',
  },
  CIC_REBOOT: {
    displayName: 'Reboot CIC',
  },
  CIC_NETWORK_SET: {
    displayName: 'Set CIC Network Parameters',
  },
  REBOOT_RELAY: {
    displayName: 'Reboot Relay',
  },
  RELAY_ON: {
    displayName: 'Relay On',
  },
  RELAY_OFF: {
    displayName: 'Relay Off',
  },
  ARM_INTRUSION_SENSOR: {
    displayName: 'Arm Intrusion Sensor',
  },
  DISARM_INTRUSION_SENSOR: {
    displayName: 'Disarm Intrusion Sensor',
  },
  OVERRIDE_INTRUSION_SENSOR: {
    displayName: 'Override Intrusion Sensor',
  },
  CONFIGURE_INTRUSION_SENSOR: {
    displayName: 'Configure Intrusion Sensor',
  },
  INTRUSION_ALARM_SETTINGS_V2: {
    displayName: 'Configure Intrusion Sensor',
  },
  ALL_ZONES_ON: {
    displayName: 'All Zones On',
  },
  ALL_ZONES_OFF: {
    displayName: 'All Zones Off',
  },
  CONFIGURE_RELAY_MONITOR: {
    displayName: 'Configure Relay Monitor',
  },
  VANTAGE_CONTROL: {
    displayName: 'Vantage Control Command',
  },
};

const getCommandNameCreator = mapping => command => {
  const commandDisplayName = command.get('display_name', 'None');
  const displayName = _.get(mapping, [command.get('command', 'None'), 'displayName'], null);

  // short circuit if displayName is null
  // and return the display name that was actually returned
  if (displayName == null) {
    return commandDisplayName;
  }

  // otherwise, get the weird cases
  const data = command.get('data', {});
  // single relay on / off
  if (_.has(data, 'relay_number')) {
    return displayName.replace('Relay', 'Relay ' + _.get(data, 'relay_number', 0));
  }
  // Relay On / Relay off with paired relays
  if (_.hasIn(data, ['packets', 0, 'relay_number']) && _.startsWith(command, 'RELAY')) {
    const relayNumbers = _.map(data.packets, entry => entry.relay_number);
    return displayName.replace('Relay', 'Relay ' + relayNumbers.join(' & '));
  }
  // Reboot Relay
  if (_.hasIn(data, ['packets', 0, 'relay_number'])) {
    const relayNumber = _.get(data.packets[0], 'relay_number');
    return displayName.replace('Relay', 'Relay ' + relayNumber);
  }
  return displayName;
};

/** expects command of type Immutable.Map * */
export const getAdminCommandName = getCommandNameCreator(adminCommandMap);

/** expects command of type Immutable.Map * */
export const getUserCommandName = getCommandNameCreator(userCommandMap);

export const getDisplayForCommandName = commandName => {
  // check user map first
  if (_.has(userCommandMap, commandName)) {
    return _.get(userCommandMap, [commandName, 'displayName'], 'No command Found');
  }
  // check admin map
  return _.get(adminCommandMap, [commandName, 'displayName'], 'No command Found');
};

/**
 * Get enhanced command display name with relay number for plain objects (not Immutable.Map).
 * Use this for API responses that return plain JS objects.
 * @param {Object} row - Command row with command, display_name, and data fields
 * @returns {string} Enhanced display name with relay number if applicable
 */
export const getCommandDisplayName = row => {
  if (!row) return 'Unknown Command';

  const commandType = row.command || '';
  const displayName = row.display_name || commandType || 'Unknown Command';
  const data = row.data || {};

  // Check if this is a relay command that needs enhancement
  const isRelayCommand = commandType.includes('RELAY') || displayName.toLowerCase().includes('relay');

  if (!isRelayCommand) {
    return displayName;
  }

  // Single relay on/off - data has relay_number directly
  if (_.has(data, 'relay_number')) {
    const relayNum = _.get(data, 'relay_number', 0);
    // Only replace if not already has a number
    if (!/Relay \d/.test(displayName)) {
      return displayName.replace('Relay', `Relay ${relayNum}`);
    }
    return displayName;
  }

  // Relay On/Off with paired relays or Reboot Relay - data.packets array
  if (_.hasIn(data, ['packets', 0, 'relay_number'])) {
    const relayNumbers = _.map(data.packets, entry => entry.relay_number);
    // Only replace if not already has a number
    if (!/Relay \d/.test(displayName)) {
      return displayName.replace('Relay', `Relay ${relayNumbers.join(' & ')}`);
    }
    return displayName;
  }

  return displayName;
};
